/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import TradeList from 'src/components/Trade/TradeList';
import TradeListPlaceholder from 'src/components/Trade/TradeList/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const tradeListRouteQuery = graphql`
  query TradeListRoute_Query(
    $uuid: ID!
  ) {
      ...TradeList
        @arguments(
          uuid: $uuid
        )
  }
`;

const tradeListRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        date: PropTypes.string,
        tz: PropTypes.string,
      }),
    }),
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {string} [props.queryDate] - ISO date string for the trade list date (from the URL query).
 *   Absent when the URL contains no date query parameter.
 * @param {string} [props.queryTimezone] - IANA timezone identifier from the URL query.
 *   Absent when the URL contains no `tz` query parameter.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade list route content.
 */
function TradeListPreloadedQueryRenderer({
  routeProps,
  queryRef,
  queryDate = null,
  queryTimezone = null,
}) {
  const queryData = usePreloadedQuery(tradeListRouteQuery, queryRef);

  return (
    <TradeList
      {...routeProps}
      {...queryData}
      queryDate={queryDate}
      queryTimezone={queryTimezone}
    />
  );
}

TradeListPreloadedQueryRenderer.propTypes = {
  routeProps: tradeListRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryDate: PropTypes.string,
  queryTimezone: PropTypes.string,
};

/**
 * Hook-safe renderer for the `TradeList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with `key={routeProps.match.params.uuid}`
 * so a uuid change disposes the stale `useQueryLoader` query.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade list route content.
 */
function TradeListRouteRenderer({
  routeProps,
  routeError = null,
}) {
  const { match } = routeProps || {};
  const { location } = match || {};
  const { query } = location || {};
  const { date: queryDate, tz: queryTimezone } = query || {};
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeListRouteQuery,
    routeProps,
    { logLabel: 'TradeListRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={TradeListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
            queryDate={queryDate}
            queryTimezone={queryTimezone}
          />
        ) : (
          <TradeList
            {...routeProps}
            queryDate={queryDate}
            queryTimezone={queryTimezone}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeListRouteRenderer.propTypes = {
  routeProps: tradeListRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeList route state supplied by found-relay.
 * Any network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it before pending props resolve. The
 * renderer is keyed on the route uuid so navigation between properties remounts
 * it and disposes the stale `useQueryLoader` query.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeList route content or loading state.
 */
export function tradeListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeListRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeListRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default TradeListRouteRenderer;
